import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../contexts/AppContext";
import api from "../lib/api";
import { toast } from "sonner";
import { Layout } from "../components/Layout";
import { BeeLogo } from "../components/BeeLogo";
import t from "../lib/i18n";
import { ChevronRight, ChevronLeft } from "lucide-react";

const STEPS = [
  { key: "child_age", options: ["3-5", "6-7", "8-10"], multi: false },
  {
    key: "learning_needs",
    options: ["Letters & reading", "Numbers & shapes", "Emotional learning", "Mindfulness", "Creativity", "Nature"],
    multi: true,
  },
  {
    key: "sensory_sensitivity",
    options: ["Very sensitive", "Somewhat sensitive", "Average"],
    multi: false,
  },
  {
    key: "goals",
    options: ["Calmer routines", "Better sleep", "Emotional regulation", "Confidence", "Focus", "Joy"],
    multi: true,
  },
  { key: "language", options: ["English", "Español"], multi: false },
  {
    key: "bedtime_challenges",
    options: ["Hard to wind down", "Anxious at night", "Restless sleep", "None"],
    multi: false,
  },
  {
    key: "emotional_support_needs",
    options: ["Big feelings", "Worry", "Frustration", "Shyness", "All of the above"],
    multi: false,
  },
];

// Map raw English option labels to i18n keys (preserves backend storage in EN)
const OPT_KEY = {
  "Letters & reading": "onbOpt_Letters",
  "Numbers & shapes": "onbOpt_Numbers",
  "Emotional learning": "onbOpt_EmotionalLearning",
  "Mindfulness": "onbOpt_Mindfulness",
  "Creativity": "onbOpt_Creativity",
  "Nature": "onbOpt_Nature",
  "Very sensitive": "onbOpt_VerySensitive",
  "Somewhat sensitive": "onbOpt_SomewhatSensitive",
  "Average": "onbOpt_Average",
  "Calmer routines": "onbOpt_CalmerRoutines",
  "Better sleep": "onbOpt_BetterSleep",
  "Emotional regulation": "onbOpt_EmotionalRegulation",
  "Confidence": "onbOpt_Confidence",
  "Focus": "onbOpt_Focus",
  "Joy": "onbOpt_Joy",
  "Hard to wind down": "onbOpt_HardToWindDown",
  "Anxious at night": "onbOpt_AnxiousAtNight",
  "Restless sleep": "onbOpt_RestlessSleep",
  "None": "onbOpt_None",
  "Big feelings": "onbOpt_BigFeelings",
  "Worry": "onbOpt_Worry",
  "Frustration": "onbOpt_Frustration",
  "Shyness": "onbOpt_Shyness",
  "All of the above": "onbOpt_AllOfTheAbove",
};

export default function Onboarding() {
  const { refreshMe, language, parent } = useApp();
  const navigate = useNavigate();
  const [step, setStep] = useState(-1); // -1 = parent welcome intro, 0..N = child questions
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState(false);

  const cur = step >= 0 ? STEPS[step] : null;
  const value = cur ? answers[cur.key] : null;

  const choose = (opt) => {
    if (!cur) return;
    setAnswers((prev) => {
      if (cur.multi) {
        const arr = prev[cur.key] || [];
        return { ...prev, [cur.key]: arr.includes(opt) ? arr.filter((x) => x !== opt) : [...arr, opt] };
      }
      return { ...prev, [cur.key]: opt };
    });
  };

  const next = async () => {
    if (step < STEPS.length - 1) return setStep(step + 1);
    setBusy(true);
    try {
      await api.post("/auth/onboarding", {
        child_age: answers.child_age || "3-5",
        learning_needs: answers.learning_needs || [],
        sensory_sensitivity: answers.sensory_sensitivity || "Average",
        goals: answers.goals || [],
        language: answers.language === "Español" ? "es" : "en",
        bedtime_challenges: answers.bedtime_challenges || null,
        emotional_support_needs: answers.emotional_support_needs || null,
      });
      await refreshMe();
      toast.success(t(language, "onbReady"));
      navigate("/profiles");
    } catch (e) {
      toast.error(t(language, "onbSaveFailed"));
    } finally {
      setBusy(false);
    }
  };

  const isSelected = (opt) => (cur && cur.multi ? (value || []).includes(opt) : value === opt);
  const canNext = cur ? (cur.multi ? (value || []).length > 0 : !!value) : true;

  // For age range / "English"/"Español" labels, render raw; otherwise translate via OPT_KEY.
  const optLabel = (opt) => {
    if (OPT_KEY[opt]) return t(language, OPT_KEY[opt]);
    return opt;
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto bn-fade">
        <div className="flex justify-center mb-5">
          <BeeLogo size={56} variant="portrait" className="bn-float" />
        </div>

        {/* Parent welcome intro — clearly separates parent account from child profile setup */}
        {step === -1 && (
          <div className="bn-card p-8 md:p-10 text-center" data-testid="onboarding-welcome">
            <div className="text-xs font-semibold tracking-[0.22em] uppercase mb-3" style={{ color: "var(--bn-text-secondary)" }}>
              {t(language, "onbWelcomeTag")}
            </div>
            <h1 className="display text-3xl md:text-4xl font-semibold mb-6">
              {(() => {
                const tmpl = t(language, "onbWelcomeTitle");
                const name = (parent?.name || "").trim();
                return name ? tmpl.replace("{name}", name) : tmpl.replace(", {name}", "").replace("{name}", "");
              })()}
            </h1>
            <div className="space-y-4 max-w-lg mx-auto text-left" style={{ color: "var(--bn-text-secondary)" }}>
              <p className="leading-relaxed">{t(language, "onbWelcomeBody1")}</p>
              <p className="leading-relaxed">{t(language, "onbWelcomeBody2")}</p>
              <p className="leading-relaxed text-sm italic">{t(language, "onbWelcomeBody3")}</p>
            </div>
            <button
              onClick={() => setStep(0)}
              className="bn-pill bn-primary-bg mt-8"
              data-testid="onboarding-begin"
            >
              {t(language, "onbWelcomeBegin")}
            </button>
          </div>
        )}

        {step >= 0 && (
          <>
            <div className="mb-8">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-semibold tracking-[0.18em] uppercase" style={{ color: "var(--bn-accent-terracotta)" }}>
                  {t(language, "onbAboutChildTag")}
                </div>
                <div className="text-sm font-semibold" style={{ color: "var(--bn-text-secondary)" }}>
                  {t(language, "onbStepProgress")} {step + 1} {t(language, "onbOf")} {STEPS.length}
                </div>
              </div>
              <div
                className="h-2 rounded-full overflow-hidden"
                style={{ background: "var(--bn-surface)" }}
                role="progressbar"
                aria-valuemin={1}
                aria-valuemax={STEPS.length}
                aria-valuenow={step + 1}
              >
                <div
                  className="h-full transition-all duration-700"
                  style={{
                    width: `${((step + 1) / STEPS.length) * 100}%`,
                    background: "var(--bn-primary)",
                  }}
                  data-testid="onboarding-progress"
                />
              </div>
            </div>

            <div className="bn-card p-8 md:p-10">
              <h2 className="display text-2xl md:text-3xl font-semibold mb-8" data-testid="onboarding-question">
                {t(language, `onbQ_${cur.key}`)}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {cur.options.map((opt) => (
                  <button
                    key={opt}
                    onClick={() => choose(opt)}
                    className="text-left rounded-2xl px-5 py-4 transition-all duration-300 font-semibold"
                    style={{
                      background: isSelected(opt) ? "var(--bn-primary)" : "var(--bn-surface-alt)",
                      border: `2px solid ${isSelected(opt) ? "var(--bn-primary)" : "var(--bn-border)"}`,
                      color: "var(--bn-text-primary)",
                    }}
                    data-testid={`onboarding-option-${opt.replace(/\s/g, '-').toLowerCase()}`}
                    aria-pressed={isSelected(opt)}
                  >
                    {optLabel(opt)}
                  </button>
                ))}
              </div>

              <div className="flex justify-between mt-10">
                <button
                  onClick={() => setStep(step === 0 ? -1 : step - 1)}
                  className="bn-pill flex items-center gap-2"
                  style={{ background: "var(--bn-surface)" }}
                  data-testid="onboarding-back"
                >
                  <ChevronLeft size={16} aria-hidden="true" /> {t(language, "onbBack")}
                </button>
                <button
                  onClick={next}
                  disabled={!canNext || busy}
                  className="bn-pill bn-primary-bg flex items-center gap-2 disabled:opacity-50"
                  data-testid="onboarding-next"
                >
                  {step === STEPS.length - 1 ? t(language, "onbFinish") : t(language, "onbNext")} <ChevronRight size={16} aria-hidden="true" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
